import { useCallback, useEffect, useRef, useState } from 'react'
import { PageLink, PageTitle } from '../../../_theme/layout/core'
import { ManageProjectToolbar } from '../../../_theme/layout/components/toolbar/ManageProjectToolbar'
import { Content } from '../../../_theme/layout/components/Content'
import { getProjectStages, ISortProject, IProject } from '../../modules/project'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../modules/auth'
import NotFound from '../../modules/apps/components/NotFound'
import Loader from '../../modules/apps/components/Loader'
import { useSnackbar } from '../../../_theme/context/SnackbarContext'
import { envConfig, getProjectEvaluationLink } from '../../../utils/config'
import PESLayout2 from '../../modules/project/PanopticEvaluationSummary/PESLayout2'
import { Button, Card } from 'react-bootstrap'
import { ArrowLeft } from 'lucide-react'
import { handleExportSummaryReportAPI } from '../../modules/core/_panoptic_evaluation'
import { getApiErrorMessage } from "../../../utils/utils";
import { isAbortedError } from "../../../utils/global";

const PanopticEvaluationSummaryPreview = () => {
    const data = Object.fromEntries(new URLSearchParams(window.location.search));
    const { auth } = useAuth();
    const { project_id } = useParams();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [errorTitle, setErrorTitle] = useState<string>("");
    const [project, setProject] = useState<ISortProject>()
    const abortRef = useRef<AbortController | null>(null);

    const fetchProjectStages = useCallback(async (project_id: string) => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            setLoading(true);
            const projectStageResponse = await getProjectStages(project_id, controller.signal);
            if (controller.signal.aborted) return;
            if (projectStageResponse.status === "ok") {
                setProject(projectStageResponse.data.project)
            } else if (projectStageResponse.errorCode == "UNAUTHORIZED_PROJECT_ACCESS") {
                setErrorMessage(projectStageResponse.message)
                setErrorTitle("Unauthorized Access !")
            } else {
                showSnackbar(projectStageResponse.message, 'danger');
                setErrorTitle("Failed to fetch projects !")
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            console.error('Failed to fetch projects:', error);
            showSnackbar(getApiErrorMessage(error, "Failed to fetch projects:"), 'danger');
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [showSnackbar]);

    const handleExportSummaryReport = useCallback(
        async () => {
            setExportLoading(true);
            try {
                const response = await handleExportSummaryReportAPI(Number(project_id));
                if (response.status === "ok") {
                    const fileUrl = `${response.data.report_url}?${Date.now()}`;
                    window.open(fileUrl, "_blank");
                } else {
                    showSnackbar(response.message, "danger");
                }
            } catch (error) {
                console.error(error);
            } finally {
                setExportLoading(false);
            }
        },
        [showSnackbar],
    );

    useEffect(() => {
        if (project_id) {
            fetchProjectStages(project_id);
        }
        return () => abortRef.current?.abort();
    }, [project_id, fetchProjectStages])

    if (loading) return <Loader loadingText='Loading projects…' />;

    if (!project) {
        return <NotFound notFoundText={errorMessage} notFoundTitle={errorTitle} />;
    }

    const breadCrumbs: Array<PageLink> = [
        {
            title: 'Panoptic Evaluation',
            path: `/pet/${project_id}`,
            isSeparator: false,
            isActive: false,
        },
        {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
        },
        {
            title: 'Panoptic Evaluation Summary Preview',
            path: ``,
            isSeparator: false,
            isActive: false,
        },
    ];

    const description = <div className='d-flex align-items-center fs-6'>
        <span>{project.sector_name}</span>
        <span className="bullet bg-gray-800 opacity-75 w-5px h-2px mx-1"></span>
        <span>{project.department_name}</span>
        <span className="bullet bg-gray-800 opacity-75 w-5px h-2px mx-1"></span>
        <span>{project.location_name}</span>
    </div>;

    const rightSection = (
        <div className="d-flex flex-column align-items-end justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2">
                <Button
                    className="btn btn-sm btn-primary"
                    disabled={exportLoading}
                    onClick={() => handleExportSummaryReport()}
                >
                    Export{" "}
                    {exportLoading && (
                        <span className="spinner-border spinner-border-sm ms-2"></span>
                    )}
                </Button>
                <Button
                    className="btn btn-light-primary btn-sm"
                    onClick={() => {
                        if (!project_id) return;
                        navigate(getProjectEvaluationLink(project_id));
                    }}
                >
                    <ArrowLeft size={12} className="me-1" />
                    Back
                </Button>
            </div>
            <p className="mb-0 fs-2 fw-bold">Panoptic Evaluation Summary </p>
        </div>
    );

    return (
        <>
            <Content>
                <Card className='mt-5 p-6'>
                    <PageTitle sitetitle={envConfig.APP_GOV_TITLE} breadcrumbs={breadCrumbs} description={description}>{project.name}</PageTitle>
                    <ManageProjectToolbar customPadding='p-0' extraClass='p-0 flex-column align-items-start align-items-xl-end flex-xl-row' rightSection={rightSection} />
                </Card>
                <div className='my-5'>
                    <PESLayout2
                        project={project as unknown as IProject}
                    />
                </div>
            </Content>
        </>
    )
}

export { PanopticEvaluationSummaryPreview }
